import { useState } from "react";
import { api, formatApiError } from "../lib/api";
import { usePageContent, show } from "../lib/usePageContent";
import { Mail, Sprout, Instagram, Facebook } from "lucide-react";

export default function ComingSoon() {
  const { content } = usePageContent("coming_soon");
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!email.trim()) return;
    setSubmitting(true);
    try {
      await api.post("/newsletter", { email: email.trim().toLowerCase() });
      setSubmitted(true);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Impossible d'enregistrer ton email pour le moment.");
    } finally {
      setSubmitting(false);
    }
  };

  const hasTopStrip = show(content, "top_strip_left") || show(content, "top_strip_right");
  const hasFooterStrip = show(content, "footer_copyright") || show(content, "footer_date");

  return (
    <div
      className="min-h-screen w-full bg-[#1A1A18] text-[#FAF8F3] flex flex-col relative overflow-hidden"
      data-testid="coming-soon-page"
    >
      {/* Subtle dot grid */}
      <div
        className="absolute inset-0 opacity-[0.06] pointer-events-none"
        style={{
          backgroundImage: "radial-gradient(rgba(255,255,255,0.6) 1px, transparent 1px)",
          backgroundSize: "24px 24px",
        }}
      />

      {/* Top mono strip */}
      {hasTopStrip && (
        <div className="relative border-b border-[#FAF8F3]/15">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-3 font-mono-label text-[10px] flex justify-between items-center gap-4">
            {show(content, "top_strip_left") ? <span className="opacity-70">{content.top_strip_left}</span> : <span />}
            {show(content, "top_strip_right") && (
              <span className="text-[#C4944A]" data-testid="coming-soon-status">{content.top_strip_right}</span>
            )}
          </div>
        </div>
      )}

      {/* Main content */}
      <main className="relative flex-1 flex items-center">
        <div className="max-w-6xl mx-auto w-full px-4 sm:px-6 lg:px-8 py-16 md:py-24 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          {/* Left column — brand + copy */}
          <div className="lg:col-span-7">
            {show(content, "brand_name") && content.brand_name && (
              <div className="mb-10">
                <span className="font-display text-4xl md:text-5xl text-[#FAF8F3]" style={{ letterSpacing: "0.02em" }} data-testid="coming-soon-brand">
                  {content.brand_name}
                </span>
              </div>
            )}

            {show(content, "status_label") && (
              <div className="font-mono-label text-[#C4944A] mb-4 flex items-center gap-2">
                <Sprout size={14} /> {content.status_label}
              </div>
            )}

            {show(content, "main_title") && (
              <h1 className="font-display text-5xl sm:text-6xl md:text-7xl lg:text-[88px] leading-[0.95] mb-6 whitespace-pre-line">
                {content.main_title}
              </h1>
            )}

            {show(content, "quote") && (
              <p className="font-quote italic text-xl md:text-2xl text-[#FAF8F3]/80 leading-snug max-w-xl mb-8 whitespace-pre-line">
                {content.quote}
              </p>
            )}

            {(show(content, "description") || show(content, "tagline")) && (
              <p className="text-base md:text-lg text-[#FAF8F3]/70 leading-relaxed max-w-xl whitespace-pre-line">
                {show(content, "description") && content.description}
                {show(content, "description") && show(content, "tagline") && content.tagline && <br />}
                {show(content, "tagline") && content.tagline && (
                  <span className="font-mono-label text-[#C4944A] mt-3 inline-block uppercase">{content.tagline}</span>
                )}
              </p>
            )}
          </div>

          {/* Right column — newsletter form */}
          <div className="lg:col-span-5">
            <div className="border-2 border-[#FAF8F3]/20 bg-[#FAF8F3]/[0.03] p-6 md:p-8 backdrop-blur-sm">
              {!submitted ? (
                <form onSubmit={handleSubmit} className="space-y-5" data-testid="coming-soon-form">
                  {show(content, "newsletter_section_label") && (
                    <div className="font-mono-label text-[#C4944A] flex items-center gap-2 uppercase">
                      <Mail size={14} /> {content.newsletter_section_label}
                    </div>
                  )}
                  {show(content, "newsletter_title") && (
                    <h2 className="font-display text-2xl md:text-3xl leading-tight whitespace-pre-line">
                      {content.newsletter_title}
                    </h2>
                  )}
                  {show(content, "newsletter_description") && (
                    <p className="text-sm text-[#FAF8F3]/70 leading-relaxed whitespace-pre-line">
                      {content.newsletter_description}
                    </p>
                  )}

                  <div>
                    <label htmlFor="cs-email" className="sr-only">Adresse email</label>
                    <input
                      id="cs-email"
                      type="email"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder={content.newsletter_email_placeholder || ""}
                      autoComplete="email"
                      className="w-full bg-transparent border-b-2 border-[#FAF8F3]/40 py-3 px-0 focus:outline-none focus:border-[#C4944A] text-base placeholder:text-[#FAF8F3]/30"
                      data-testid="coming-soon-email-input"
                    />
                  </div>

                  {error && (
                    <div className="text-sm text-[#C4944A]" data-testid="coming-soon-error">
                      {error}
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={submitting || !email.trim()}
                    className="w-full bg-[#4A7A3D] text-[#FAF8F3] px-6 py-4 font-mono-label hover:bg-[#3E6633] disabled:bg-[#4A7A3D]/40 disabled:cursor-not-allowed transition-colors uppercase"
                    data-testid="coming-soon-submit"
                  >
                    {submitting ? "Enregistrement…" : (content.newsletter_button_label || "Me prévenir →")}
                  </button>
                </form>
              ) : (
                <div className="space-y-5 py-2" data-testid="coming-soon-success">
                  <div className="font-mono-label text-[#C4944A] flex items-center gap-2 uppercase">
                    <Mail size={14} /> Inscription confirmée
                  </div>
                  {show(content, "newsletter_success_title") && (
                    <h2 className="font-display text-3xl md:text-4xl leading-tight text-[#C4944A]">
                      {content.newsletter_success_title}
                    </h2>
                  )}
                  {show(content, "newsletter_success_message") && (
                    <p className="text-base text-[#FAF8F3]/85 leading-relaxed whitespace-pre-line">
                      {content.newsletter_success_message}
                    </p>
                  )}
                  <button
                    type="button"
                    onClick={() => { setSubmitted(false); setEmail(""); }}
                    className="font-mono-label text-[#FAF8F3]/60 hover:text-[#C4944A] underline"
                    data-testid="coming-soon-reset"
                  >
                    Inscrire un autre email
                  </button>
                </div>
              )}
            </div>

            {/* Social */}
            {(show(content, "social_label") || show(content, "social_facebook") || show(content, "social_instagram")) && (
              <div className="mt-6 flex items-center gap-4 text-[#FAF8F3]/60">
                {show(content, "social_label") && content.social_label && (
                  <span className="font-mono-label text-xs uppercase">{content.social_label}</span>
                )}
                {show(content, "social_facebook") && content.social_facebook && (
                  <a href={content.social_facebook} target="_blank" rel="noopener noreferrer" className="hover:text-[#C4944A] inline-flex items-center justify-center min-h-[44px] min-w-[44px]" aria-label="Facebook" data-testid="coming-soon-social-facebook">
                    <Facebook size={18} />
                  </a>
                )}
                {show(content, "social_instagram") && content.social_instagram && (
                  <a href={content.social_instagram} target="_blank" rel="noopener noreferrer" className="hover:text-[#C4944A] inline-flex items-center justify-center min-h-[44px] min-w-[44px]" aria-label="Instagram" data-testid="coming-soon-social-instagram">
                    <Instagram size={18} />
                  </a>
                )}
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Footer */}
      {hasFooterStrip && (
        <footer className="relative border-t border-[#FAF8F3]/15">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 font-mono-label text-[10px] text-[#FAF8F3]/50">
            {show(content, "footer_copyright") ? <span>{content.footer_copyright}</span> : <span />}
            {show(content, "footer_date") && <span>{content.footer_date}</span>}
          </div>
        </footer>
      )}
    </div>
  );
}
